import { createRootRoute, Outlet } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import {
  Toaster,
  SidebarProvider,
  SidebarInset,
  AllRightsReservedFooter,
  TopNavbar,
  Sentry,
} from '@org/ui';
import React, { useMemo } from 'react';
import { withAuthenticationRequired, useAuth0 } from '@auth0/auth0-react';
import { AppSidebar } from '../components/app-sidebar';
import { TeacherPicker, TeacherProvider } from '../components/teacher-picker';
import { NotificationBell } from '../components/notification-bell';

type AuthUser = ReturnType<typeof useAuth0>['user'];

function RootLayoutInner() {
  const { user } = useAuth0();

  return (
    <TeacherProvider>
      <SidebarProvider defaultOpen={false} className="bg-[#fff8fb]">
        <AppSidebar />
        <ShellContent user={user} />
      </SidebarProvider>
    </TeacherProvider>
  );
}

function ShellContent({ user }: { user: AuthUser }) {
  return (
    <SidebarInset className="bg-[#fff8fb]">
      <div className="flex min-h-svh flex-col">
        <TopNavbar
          eyebrowText="Teacher dashboard"
          displayName={user?.given_name ?? user?.nickname ?? user?.email ?? 'Professora'}
          user={user}
          app="teacher"
          extraElements={
            <>
              <TeacherPicker />
              <NotificationBell />
            </>
          }
          logoutLabel="Sair"
          loggingOutLabel="Saindo..."
        />
        <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-10">
          <div className="mx-auto w-full max-w-6xl">
            <Outlet />
          </div>
        </main>
        <AllRightsReservedFooter className="bg-[#fff8fb]" />
        <TanStackRouterDevtools />
        <Toaster />
      </div>
    </SidebarInset>
  );
}

function RootLayout() {
  const isBypassEnabled = import.meta.env.VITE_E2E_AUTH_BYPASS === 'true';
  const GuardedRoot = useMemo(() => {
    if (isBypassEnabled) {
      return RootLayoutInner;
    }

    return withAuthenticationRequired(RootLayoutInner, {
      onRedirecting: () => (
        <div className="flex min-h-screen items-center justify-center">
          <span>Redirecionando para login...</span>
        </div>
      ),
    });
  }, [isBypassEnabled]);

  const GuardComponent = GuardedRoot;

  return <GuardComponent />;
}

function RouteErrorComponent({ error }: { error: Error }) {
  Sentry.captureException(error, { tags: { source: 'tanstack-router-root' } });
  return (
    <div style={{ padding: 24, fontFamily: 'system-ui, sans-serif' }}>
      <h2>This page failed to load</h2>
      <p>Please refresh to try again.</p>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: RouteErrorComponent,
})
